import axios from "axios";
import CryptoJS from "crypto-js";
import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getSessionStorageValue } from "../../../service/getInfoSignatory";
import { getToken } from "../../../service/getToken";
import { formatCPF } from "../../../utils/formats";
import { isDocumentValid, isEmailValid } from "../../../utils/validations";
import LoadingSpinner from "../../LoadingSpinner";
import {
  Button,
  ButtonBack,
  Container,
  ContainerInfos,
  ContainerInputs,
  Form,
  FormWrapper,
  InputWrapper,
  InputWrapperCode,
  Label,
  MessageEmail,
  MessageTime,
  SubTitle,
  Title,
  Wrapper,
  WrapperButton,
} from "./styled";

interface FormData {
  document: string;
  email: string;
  verification_code: string;
}
interface FormAuthenticationProps {
  documentName: string;
  signingDeadline: string;
  reason: string;
  requester: string;
  onVerificationSuccess: () => void;
}

export const FormAuthentication: React.FC<FormAuthenticationProps> = ({
  documentName,
  signingDeadline,
  reason,
  requester,
  onVerificationSuccess,
}) => {
  const [errorCpf, setErrorCpf] = useState<boolean>(false);
  const [errorEmail, setErrorEmail] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorVerificationCode, setErrorVerificationCode] =
    useState<boolean>(false);
  const [showVerification, setShowVerification] = useState<boolean>(false);

  const url = window.location.search;
  const urlParams = new URLSearchParams(url);
  const id = urlParams.get("id");
  const code = urlParams.get("code");
  const hasCode = code !== null && code !== "";
  const stepVerification = hasCode || showVerification;
  const [formData, setFormData] = useState<FormData>({
    document: "",
    email: "",
    verification_code: "",
  });
  const navigate = useNavigate();

  const email = getSessionStorageValue("email");
  const documentNumber = getSessionStorageValue("document");

  useEffect(() => {
    if (showVerification) {
      onVerificationSuccess();
    }
  }, [showVerification, onVerificationSuccess]);

  useEffect(() => {
    if (code) {
      const encryptMethodLength = () => {
        const method = "AES-256-CBC";
        const matchResult = method.match(/\d+/);
        const aesNumber = matchResult ? matchResult[0] : "";
        return parseInt(aesNumber);
      };

      const decrypt = ({ encryptedString, key }: any) => {
        const json = JSON.parse(
          CryptoJS.enc.Utf8.stringify(
            CryptoJS.enc.Base64.parse(encryptedString)
          )
        );

        const salt = CryptoJS.enc.Hex.parse(json.salt);
        const iv = CryptoJS.enc.Hex.parse(json.iv);

        const encrypted = json.ciphertext;

        let iterations = parseInt(json.iterations);
        if (iterations <= 0) {
          iterations = 999;
        }
        const methodLength = encryptMethodLength() / 4;
        const hashKey = CryptoJS.PBKDF2(key, salt, {
          hasher: CryptoJS.algo.SHA512,
          keySize: methodLength / 8,
          iterations: iterations,
        });

        const decrypted = CryptoJS.AES.decrypt(encrypted, hashKey, {
          mode: CryptoJS.mode.CBC,
          iv: iv,
        });
        return decrypted.toString(CryptoJS.enc.Utf8);
      };

      const key = process.env.REACT_APP_SECRET as string;

      try {
        const mensagem_descriptografada = decrypt({
          encryptedString: code,
          key,
        });
        const jsonParsed = mensagem_descriptografada.split("|");
        setFormData({
          document: jsonParsed[2],
          email: jsonParsed[1],
          verification_code: jsonParsed[0],
        });
      } catch (e) {
        console.error(e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    if (name === "document") {
      const cleanedValue = value.replace(/\D/g, "");
      const formattedCPF = formatCPF(cleanedValue);
      setFormData({
        ...formData,
        [name]: formattedCPF,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    if (name === "document") {
      setErrorCpf(false);
    } else if (name === "email") {
      setErrorEmail(false);
    } else if (stepVerification && name === "verification_code") {
      setErrorVerificationCode(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const url = process.env.REACT_APP_API_BASEURL;

    const urlID = window.location.search;
    const urlParams = new URLSearchParams(urlID);
    const id = urlParams.get("id");

    const isCpfValid = isDocumentValid(formData.document);
    const isEmailEmpty = !formData.email || formData.email.trim() === "";

    try {
      const token = await getToken();

      if (!hasCode) {
        if (!isCpfValid) {
          setErrorCpf(true);
          return;
        } else {
          setErrorCpf(false);
        }
        if (isEmailEmpty || !isEmailValid(formData.email.trim())) {
          setErrorEmail(true);
          return;
        } else {
          setErrorEmail(false);
        }
      }

      if (!stepVerification) {
        let config = {
          method: "post",
          maxBodyLength: Infinity,
          url: `${url}/signatories/auth/verification/${id}/check`,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          data: JSON.stringify({
            document: formData.document,
            email: formData.email.trim(),
            document_signature_request_uuid: id,
          }),
        };

        TagManager.dataLayer({
          dataLayer: {
            event: "FirstFormSubmission",
            dataLayer: {
              userProject: "Assinando",
              page: "auth",
              formData: {
                document: formData.document,
                email: formData.email.trim(),
              },
            },
          },
        });
        try {
          setIsLoading(true);
          const response = await axios.request(config);
          sessionStorage.setItem("email", formData.email);
          sessionStorage.setItem("document", formData.document);
          if (response.data) {
            setShowVerification(true);
            setIsLoading(false);
          } else {
            setIsLoading(false);
            console.error("Failed to start verification");
          }
        } catch (error: any) {
          setIsLoading(false);
          Swal.fire({
            title: "Erro!",
            text: error.response.data.errors.error,
            icon: "error",
            confirmButtonText: "OK ",
            confirmButtonColor: "#355B94",
          });
          console.error("Network error:", error);
        }
      } else {
        let config = {
          method: "post",
          maxBodyLength: Infinity,
          url: `${url}/signatories/auth/verification/${id}`,
          data: JSON.stringify({
            document: documentNumber || formData.document,
            email: email || formData.email,
            verification_code: formData.verification_code,
          }),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        TagManager.dataLayer({
          dataLayer: {
            event: "FirstFormSubmission",
            dataLayer: {
              userProject: "Assinando",
              page: "auth",
              formData: {
                document: formData.document,
                email: formData.email.trim(),
                verification_code: formData.verification_code,
              },
            },
          },
        });
        if (!(document || formData.document) || !(email || formData.email)) {
          setIsLoading(false);
          Swal.fire({
            title: "Erro!",
            text: "Adquira o código novamente.",
            icon: "error",
            confirmButtonText: "OK ",
            confirmButtonColor: "#355B94",
          });
          setFormData({
            document: "",
            email: "",
            verification_code: "",
          });
          setShowVerification(false);
          return;
        }

        try {
          setIsLoading(true);
          const response = await axios.request(config);
          if (response.data.status === "success") {
            setIsLoading(false);
            sessionStorage.setItem(
              "token_user",
              response.data.authorisation.token
            );
            if (sessionStorage.getItem("token_user")) {
              sessionStorage.removeItem("email");
              sessionStorage.removeItem("document");
              window.location.href = `${response.data.redirect_to}`;
            } else {
              console.error("Failed to set token_user sessionStorage");
            }
          } else {
            setIsLoading(false);
            setErrorVerificationCode(true);
            console.error("Failed to validate verification code");
          }
        } catch (error: any) {
          setIsLoading(false);
          setErrorVerificationCode(true);
          Swal.fire({
            title: "Erro!",
            text: error.response.data.errors.error,
            icon: "error",
            confirmButtonText: "OK ",
            confirmButtonColor: "#355B94",
          }).then((result) => {
            if (
              result.isConfirmed &&
              error.response.data.errors.error ===
                "Código de verificação vencido"
            ) {
              setShowVerification(false);
            }
          });
          console.error("Network error:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching token:", error);
    }
  };

  const BackButton = () => {
    setShowVerification(false);
    sessionStorage.removeItem("email");
    sessionStorage.removeItem("document");
    setFormData({
      document: "",
      email: "",
      verification_code: "",
    });
    navigate(`?id=${id}`);
  };

  return (
    <Container>
      <Title>Verificação de Identidade</Title>
      <SubTitle>Vamos começar o processo de assinatura de documento.</SubTitle>
      <Wrapper>
        {isLoading && <LoadingSpinner />}
        <Form onSubmit={handleSubmit}>
          <FormWrapper>
            {stepVerification ? (
              <>
                <InputWrapperCode>
                  <Label>Código de Verificação:</Label>
                  <input
                    type="text"
                    name="verification_code"
                    placeholder="x x x x x x"
                    value={formData.verification_code}
                    onChange={handleInputChange}
                    maxLength={6}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onInput={(e) => {
                      const inputElement = e.target as HTMLInputElement;
                      inputElement.value = inputElement.value.replace(
                        /[^0-9]/g,
                        ""
                      );
                    }}
                  />
                  {errorVerificationCode && (
                    <p style={{ fontSize: "12px", color: "red" }}>
                      Verifique o código de verificação
                    </p>
                  )}
                </InputWrapperCode>
                <MessageTime>
                  Enviamos o código de verificação para o email:{" "}
                  <MessageEmail>
                    <b>{email || formData.email}</b>
                  </MessageEmail>
                </MessageTime>
                <MessageTime>
                  O código é valido por <b>10 minutos</b>.
                </MessageTime>
              </>
            ) : (
              <>
                <ContainerInfos
                  style={{ color: "#2c2c2c", marginBottom: "10px" }}
                >
                  {requester && (
                    <p>
                      <span style={{ fontWeight: "bold" }}>Solicitante:</span>{" "}
                      {requester}
                    </p>
                  )}
                  {documentName && (
                    <p>
                      <span style={{ fontWeight: "bold" }}>
                        Nome da requisição:
                      </span>{" "}
                      {documentName}
                    </p>
                  )}
                  {signingDeadline && (
                    <p>
                      <span style={{ fontWeight: "bold" }}>
                        Prazo para assinatura:
                      </span>{" "}
                      {signingDeadline}
                    </p>
                  )}
                  {reason && (
                    <p>
                      <span style={{ fontWeight: "bold" }}>Motivo:</span>{" "}
                      {reason}
                    </p>
                  )}
                </ContainerInfos>
                <ContainerInputs>
                  <InputWrapper>
                    <Label>Seu CPF</Label>
                    <input
                      type="text"
                      maxLength={14}
                      name="document"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Digite seu CPF..."
                      value={formData.document}
                      onChange={handleInputChange}
                    />
                    {errorCpf && (
                      <p style={{ fontSize: "12px", color: "red" }}>
                        Digite um CPF válido
                      </p>
                    )}
                  </InputWrapper>
                  <InputWrapper>
                    <Label>Seu E-mail</Label>
                    <input
                      type="text"
                      name="email"
                      placeholder="Digite seu e-mail..."
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                    {errorEmail && (
                      <p style={{ fontSize: "12px", color: "red" }}>
                        Digite um e-mail válido
                      </p>
                    )}
                  </InputWrapper>
                </ContainerInputs>
              </>
            )}
            <WrapperButton isSecond={stepVerification}>
              {stepVerification && (
                <ButtonBack onClick={() => BackButton()}>Voltar</ButtonBack>
              )}
              <Button
                backgroundColor="#77EC84"
                hoverBackgroundColor="#72E27F"
                type="submit"
              >
                Próximo
              </Button>
            </WrapperButton>
          </FormWrapper>
        </Form>
      </Wrapper>
    </Container>
  );
};
